<script setup lang="ts">
import AlarmResource from '@/api/resources/alarm'
import type { IAlarm } from '@/interfaces/entities/alarm'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const props = withDefaults(
  defineProps<{
    isCorrelationalAlarms?: boolean
  }>(),
  {
    isCorrelationalAlarms: false,
  },
)

const router = useRouter()
const openAlarms = ref<IAlarm[]>([])
const isLoading = ref(true)
const isMinimized = ref(false)

const fetchOpenAlarms = async () => {
  isLoading.value = true
  try {
    const res = await AlarmResource.getOpenAlarms()
    openAlarms.value = res?.data ?? []
  } catch {
    openAlarms.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchOpenAlarms)

defineExpose({ fetchOpenAlarms })

const filteredOpenAlarms = computed(() =>
  props.isCorrelationalAlarms
    ? openAlarms.value.filter((alarm) => alarm.alarm_type === 'correlational')
    : openAlarms.value.filter((alarm) => alarm.alarm_type != 'correlational'),
)

const hasAlarms = computed(() => filteredOpenAlarms.value.length > 0)
const displayAlarms = computed(() => filteredOpenAlarms.value.slice(0, 20))
const shouldRender = computed(() => !props.isCorrelationalAlarms || hasAlarms.value)

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const alarmsTypeLabels: Record<string, string> = {
  interruption: 'Interrupção',
  critical_temperature: 'Temperatura crítica',
  low_load: 'Carga baixa',
  no_data: 'Sem dados',
  door_open: 'Porta aberta',
  defrost_time: 'Tempo de degelo',
  burnt_resistor: 'Resistor queimado',
  activation_peak_hour: 'Pico de ativação',
  power_factor: 'Fator de potência',
  empty_mixer: 'Misturador vazio',
  outside_regular_time: 'Fora do horário',
  correlational: 'Correlacional'
}

function formatAlarmType(alarm: IAlarm, type: string){
  if(!type) return 'Alarme'

  if(type === 'correlational'){
    return alarm.name? alarm.name : "Alarme Correlacional"
  }

  return alarmsTypeLabels[type] ?? type
}

function goToSensor(sensorId: number) {
  router.push({ name: 'sensor-detail', params: { id: String(sensorId) } })
}

function toggleMinimized() {
  isMinimized.value = !isMinimized.value
}
</script>

<template>
  <!-- Sempre ocupa espaço: shimmer no loading, conteúdo ou "Vazio" depois -->
  <section
    v-if="shouldRender"
    class="overflow-hidden border bg-white shadow-md"
    :class="
      isCorrelationalAlarms
        ? 'border-purple-300'
        : hasAlarms && !isLoading
          ? 'border-red-300 bg-red-50/30'
          : 'border-gray-200'
    ">
    <button
      type="button"
      class="flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
      :class="[
        isCorrelationalAlarms
          ? 'bg-purple-50/50 hover:bg-purple-50/80'
          : hasAlarms
            ? 'bg-red-50/50 hover:bg-gray-50/80'
            : 'bg-gray-50/50 hover:bg-gray-50/80',
      ]"
      @click="toggleMinimized">
      <div class="flex items-center gap-2">
        <span class="text-lg font-semibold text-gray-800">
          {{ isCorrelationalAlarms ? 'Alarmes correlacionais' : 'Alertas' }}
        </span>
        <span
          v-if="isLoading && !isCorrelationalAlarms"
          class="h-6 w-24 bg-gray-200 animate-pulse" />
        <span
          v-else-if="hasAlarms"
          class="px-2.5 py-0.5 text-xs font-medium text-white"
          :class="isCorrelationalAlarms ? 'bg-purple-500' : 'bg-red-500'">
          {{ filteredOpenAlarms.length }} alarme(s) aberto(s)
        </span>
        <span
          v-else
          class="text-sm font-medium text-gray-500">
          Vazio
        </span>
      </div>
      <svg
        class="h-5 w-5 shrink-0 text-gray-500 transition-transform"
        :class="{ 'rotate-180': isMinimized }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      class="grid transition-[grid-template-rows] duration-300 ease-in-out"
      :class="isMinimized ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'">
      <div class="min-h-0 overflow-hidden">
        <div class="min-h-[4rem] border-t border-gray-100">
          <!-- Shimmer: preserva dimensão enquanto carrega -->
          <div v-if="isLoading" class="space-y-3 px-4 py-4">
            <div class="flex items-center justify-between gap-3">
              <div class="flex-1 space-y-2">
                <div class="h-4 max-w-xs bg-gray-200 animate-pulse" style="width: 75%" />
                <div class="h-3 max-w-[10rem] bg-gray-100 animate-pulse" style="width: 50%" />
              </div>
              <div class="h-6 w-14 shrink-0 bg-gray-200 animate-pulse" />
            </div>
            <div class="flex items-center justify-between gap-3">
              <div class="flex-1 space-y-2">
                <div class="h-4 max-w-xs bg-gray-200 animate-pulse" style="width: 66%" />
                <div class="h-3 w-32 bg-gray-100 animate-pulse" />
              </div>
              <div class="h-6 w-14 shrink-0 bg-gray-200 animate-pulse" />
            </div>
          </div>

          <ul v-else class="max-h-64 divide-y divide-gray-100 overflow-y-auto">
            <li
              v-for="alarm in displayAlarms"
              :key="alarm.id"
              class="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 transition-colors"
              :class="isCorrelationalAlarms ? 'hover:bg-purple-50/50' : 'hover:bg-red-50/50'"
              @click="alarm.sensor && goToSensor(alarm.sensor.id)">
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-medium text-gray-900">
                  {{ alarm.sensor?.name ?? alarm.name ?? 'Sensor' }}
                </p>
                <p class="text-xs text-gray-500">
                  {{ formatAlarmType(alarm, alarm.alarm_type) }} · Desde {{ formatDate(alarm.start_date) }}
                </p>
              </div>
              <span
                class="flex-shrink-0 px-2 py-0.5 text-xs font-medium"
                :class="isCorrelationalAlarms ? 'bg-purple-100 text-purple-800' : 'bg-red-100 text-red-800'">
                Aberto
              </span>
            </li>
            <li
              v-if="filteredOpenAlarms.length > 20"
              class="px-4 py-2 text-center text-xs text-gray-500">
              Mostrando 20 de {{ filteredOpenAlarms.length }} alarmes
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>
